import React, { useCallback, useEffect, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Plus, X, FileText, Zap, Trash2, Send, CheckCircle, RefreshCcw, Pencil,
  AlertCircle, Clock, DollarSign, FileCheck,
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

// ── Constants ─────────────────────────────────────────────────────────────────

const BILLING_PERIODS = [
  { value: 'monthly',   label: 'Månadsvis' },
  { value: 'quarterly', label: 'Kvartalsvis' },
  { value: 'annual',    label: 'Årsvis' },
];

const BILLING_PERIOD_LABELS = Object.fromEntries(BILLING_PERIODS.map((b) => [b.value, b.label]));

const STATUS_META = {
  utkast:     { label: 'Utkast',     cls: 'bg-gray-100 text-gray-700' },
  skickad:    { label: 'Skickad',    cls: 'bg-blue-100 text-blue-700' },
  betald:     { label: 'Betald',     cls: 'bg-green-100 text-green-700' },
  förfallen:  { label: 'Förfallen',  cls: 'bg-red-100 text-red-700' },
  krediterad: { label: 'Krediterad', cls: 'bg-yellow-100 text-yellow-700' },
};

const selectCls = 'w-full h-10 px-3 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring';

const EMPTY_FORM = {
  org_id: '', billing_period: 'monthly', period_start: '', period_end: '',
  due_date: '', vat_rate: '25', description: '', notes: '',
};

const EMPTY_LINE = { description: '', quantity: '1', unit_price: '' };

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(val) {
  const n = parseFloat(val) || 0;
  return n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' SEK';
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('sv-SE') : '–';
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function extractDetail(err) {
  const d = err?.response?.data?.detail;
  if (!d) return 'Åtgärden misslyckades';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || String(e)).join('; ') || 'Valideringsfel';
  return 'Åtgärden misslyckades';
}

function lineAmount(line) {
  const q = parseFloat(line.quantity) || 0;
  const p = parseFloat(line.unit_price) || 0;
  return q * p;
}

function computeTotals(lines, vatRate) {
  const netto = lines.reduce((s, l) => s + lineAmount(l), 0);
  const vat = netto * ((parseFloat(vatRate) || 0) / 100);
  return { netto, vat, total: netto + vat };
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, color }) {
  return (
    <Card data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5 flex items-start gap-4">
        <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className="text-2xl font-black mt-1" style={{ color: '#1e293b' }}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Confirm dialog ────────────────────────────────────────────────────────────

function ConfirmDialog({ open, title, message, confirmLabel, onConfirm, onCancel, danger }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-sm shadow-lg">
        <CardContent className="p-6 space-y-4">
          <h3 className="text-base font-semibold" style={{ color: '#1e293b' }}>{title}</h3>
          <p className="text-sm text-muted-foreground">{message}</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={onCancel}>Avbryt</Button>
            <Button
              size="sm"
              className={danger ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Mark-paid dialog ──────────────────────────────────────────────────────────

function MarkPaidDialog({ invoice, onDone, onCancel }) {
  const [form, setForm] = useState({
    paid_date: todayISO(),
    payment_method: '',
    payment_reference: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/invoices/${invoice.id}/mark-paid`, {
        paid_date: form.paid_date,
        payment_method: form.payment_method || null,
        payment_reference: form.payment_reference || null,
      });
      toast.success('Faktura markerad som betald');
      onDone();
    } catch (err) {
      toast.error(extractDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-sm shadow-lg">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold" style={{ color: '#1e293b' }}>Markera som betald</h3>
            <Button variant="ghost" size="icon" onClick={onCancel}><X size={16} /></Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Faktura <span className="font-mono font-semibold">{invoice.invoice_number}</span>
          </p>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="mp-date">Betalningsdatum *</Label>
              <Input
                id="mp-date"
                type="date"
                value={form.paid_date}
                onChange={set('paid_date')}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mp-method">Betalningsmetod</Label>
              <Input
                id="mp-method"
                value={form.payment_method}
                onChange={set('payment_method')}
                placeholder="t.ex. Bankgiro, Swish"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mp-ref">Referens / OCR</Label>
              <Input
                id="mp-ref"
                value={form.payment_reference}
                onChange={set('payment_reference')}
                placeholder="Valfri betalningsreferens"
              />
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <Button type="button" variant="outline" size="sm" onClick={onCancel}>Avbryt</Button>
              <Button type="submit" size="sm" disabled={submitting}>
                {submitting ? 'Sparar…' : 'Markera betald'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Batch dialog ──────────────────────────────────────────────────────────────

function BatchDialog({ open, onClose, onGenerated }) {
  const [form, setForm] = useState({ billing_period: 'monthly', period_start: '', period_end: '' });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/invoices/generate-batch', form);
      const count = res.data?.created ?? res.data?.count ?? 0;
      toast.success(`${count} fakturor genererades`);
      onGenerated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte generera fakturor');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-md shadow-lg">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold" style={{ color: '#1e293b' }}>Generera batch-fakturor</h3>
            <Button variant="ghost" size="icon" onClick={onClose}><X size={16} /></Button>
          </div>
          <p className="text-sm text-muted-foreground">Skapar utkastfakturor för alla aktiva avtal.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Faktureringsperiod</Label>
              <select className={selectCls} value={form.billing_period} onChange={set('billing_period')}>
                {BILLING_PERIODS.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Periodens start</Label>
                <Input type="date" value={form.period_start} onChange={set('period_start')} required />
              </div>
              <div className="space-y-1.5">
                <Label>Periodens slut</Label>
                <Input type="date" value={form.period_end} onChange={set('period_end')} required />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>Avbryt</Button>
              <Button type="submit" size="sm" disabled={loading}>
                {loading ? 'Genererar…' : 'Generera'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Inline form ───────────────────────────────────────────────────────────────

function InvoiceForm({ invoice, orgs, onSaved, onCancel }) {
  const isEdit = !!invoice;
  const [form, setForm] = useState(
    isEdit
      ? {
          org_id: invoice.org_id,
          billing_period: invoice.billing_period || 'monthly',
          period_start: invoice.period_start || '',
          period_end: invoice.period_end || '',
          due_date: invoice.due_date || '',
          vat_rate: String(invoice.vat_rate ?? 25),
          description: invoice.description || '',
          notes: invoice.notes || '',
        }
      : EMPTY_FORM,
  );
  const [lines, setLines] = useState(
    isEdit && invoice.lines?.length
      ? invoice.lines.map((l) => ({
          description: l.description,
          quantity: String(l.quantity),
          unit_price: String(l.unit_price),
        }))
      : [{ ...EMPTY_LINE }],
  );
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setLine = (i, k) => (e) => {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [k]: e.target.value } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const { netto, vat, total } = computeTotals(lines, form.vat_rate);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      ...form,
      vat_rate: parseFloat(form.vat_rate) || 25,
      lines: lines.map((l, idx) => ({
        description: l.description,
        quantity: parseFloat(l.quantity) || 1,
        unit_price: parseFloat(l.unit_price) || 0,
        sort_order: idx,
      })),
    };
    try {
      if (isEdit) {
        await api.patch(`/invoices/${invoice.id}`, payload);
        toast.success('Faktura uppdaterad');
      } else {
        await api.post('/invoices', payload);
        toast.success('Faktura skapad');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara faktura');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mb-6 shadow-sm border-t-4 border-t-slate-700">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold" style={{ color: '#1e293b' }}>
            {isEdit ? `Redigera ${invoice.invoice_number}` : 'Ny faktura'}
          </h3>
          <Button variant="ghost" size="icon" onClick={onCancel}><X size={16} /></Button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="inv-org">Kund *</Label>
              <select
                id="inv-org"
                className={selectCls}
                value={form.org_id}
                onChange={set('org_id')}
                required
              >
                <option value="">Välj kund…</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-period">Faktureringsperiod *</Label>
              <select
                id="inv-period"
                data-testid="billing-period-select"
                className={selectCls}
                value={form.billing_period}
                onChange={set('billing_period')}
              >
                {BILLING_PERIODS.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-start">Periodens start *</Label>
              <Input id="inv-start" type="date" value={form.period_start} onChange={set('period_start')} required />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-end">Periodens slut *</Label>
              <Input id="inv-end" type="date" value={form.period_end} onChange={set('period_end')} required />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-due">Förfallodag</Label>
              <Input id="inv-due" type="date" value={form.due_date} onChange={set('due_date')} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-vat">Moms (%)</Label>
              <Input
                id="inv-vat"
                type="number"
                min="0"
                max="100"
                value={form.vat_rate}
                onChange={set('vat_rate')}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inv-desc">Beskrivning</Label>
            <Input id="inv-desc" value={form.description} onChange={set('description')} placeholder="Fakturabeskrivning" />
          </div>

          {/* Lines */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Fakturarader</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLine}>
                <Plus size={14} className="mr-1" /> Lägg till rad
              </Button>
            </div>

            <div className="space-y-2">
              {lines.map((line, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-5">
                    <Input
                      placeholder="Beskrivning"
                      value={line.description}
                      onChange={setLine(i, 'description')}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      placeholder="Antal"
                      min="0"
                      step="any"
                      value={line.quantity}
                      onChange={setLine(i, 'quantity')}
                      required
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      placeholder="À-pris (SEK)"
                      min="0"
                      step="any"
                      value={line.unit_price}
                      onChange={setLine(i, 'unit_price')}
                      required
                    />
                  </div>
                  <div className="col-span-2 flex items-center justify-between pl-1">
                    <span className="text-sm text-muted-foreground text-right w-full">
                      {lineAmount(line).toLocaleString('sv-SE')}
                    </span>
                    {lines.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="ml-1 flex-shrink-0"
                        onClick={() => removeLine(i)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Live sum */}
            <div className="mt-4 flex flex-col items-end gap-1 text-sm">
              <div className="flex gap-6 text-muted-foreground">
                <span>Netto: <strong className="text-foreground">{fmt(netto)}</strong></span>
                <span>Moms: <strong className="text-foreground">{fmt(vat)}</strong></span>
              </div>
              <div
                data-testid="invoice-total"
                className="text-base font-bold"
                style={{ color: '#1e293b' }}
              >
                Totalt: {fmt(total)}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inv-notes">Interna noteringar</Label>
            <Textarea id="inv-notes" value={form.notes} onChange={set('notes')} rows={2} />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onCancel}>Avbryt</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Sparar…' : isEdit ? 'Spara ändringar' : 'Skapa faktura'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function InvoicesAdminPage() {
  const [invoices, setInvoices] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterOrg, setFilterOrg] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Inline form
  const [formOpen, setFormOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);

  // Dialogs
  const [batchOpen, setBatchOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type, invoice }
  const [markPaid, setMarkPaid] = useState(null); // { invoice }

  // Expanded row
  const [expandedId, setExpandedId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterOrg) params.org_id = filterOrg;
      if (filterStatus) params.status = filterStatus;
      const [invRes, orgRes] = await Promise.all([
        api.get('/invoices', { params }),
        api.get('/admin/customers'),
      ]);
      setInvoices(invRes.data);
      setOrgs(orgRes.data);
    } catch {
      toast.error('Kunde inte hämta fakturor');
    } finally {
      setLoading(false);
    }
  }, [filterOrg, filterStatus]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // KPI calculations
  const kpis = React.useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const outstanding = invoices.filter((i) => ['skickad', 'förfallen'].includes(i.status)).length;
    const overdue = invoices.filter((i) => i.status === 'förfallen').length;
    const paidThisMonth = invoices.filter((i) => {
      if (i.status !== 'betald' || !i.paid_date) return false;
      const d = new Date(i.paid_date);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    }).length;
    const drafts = invoices.filter((i) => i.status === 'utkast').length;
    return { outstanding, overdue, paidThisMonth, drafts };
  }, [invoices]);

  const openCreate = () => { setEditInvoice(null); setFormOpen(true); };
  const openEdit = (inv) => { setEditInvoice(inv); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditInvoice(null); };

  const handleSaved = () => { closeForm(); fetchAll(); };

  const handleConfirm = async () => {
    if (!confirm) return;
    const { type, invoice } = confirm;
    setConfirm(null);
    try {
      if (type === 'send') {
        await api.post(`/invoices/${invoice.id}/send`);
        toast.success('Faktura skickad');
      } else if (type === 'credit') {
        await api.post(`/invoices/${invoice.id}/credit`);
        toast.success('Kreditfaktura skapad');
      } else if (type === 'delete') {
        await api.delete(`/invoices/${invoice.id}`);
        toast.success('Faktura borttagen');
      }
      await fetchAll();
    } catch (err) {
      toast.error(extractDetail(err));
    }
  };

  const CONFIRM_META = {
    send: {
      title: 'Skicka faktura?',
      message: 'Fakturans status ändras till "Skickad". Är du säker?',
      label: 'Skicka',
      danger: false,
    },
    credit: {
      title: 'Kreditera faktura?',
      message: 'En kreditfaktura med negativa belopp skapas och länkas till denna.',
      label: 'Kreditera',
      danger: false,
    },
    delete: {
      title: 'Ta bort faktura?',
      message: 'Utkastet tas bort permanent. Denna åtgärd kan inte ångras.',
      label: 'Ta bort',
      danger: true,
    },
  };

  const filtered = invoices; // server already filters; local re-filter for immediate UX is optional

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1
              className="text-2xl font-black"
              data-testid="invoices-heading"
              style={{ color: '#1e293b' }}
            >
              Fakturor
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">Administrera och följ upp fakturering</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="batch-generate-btn"
              onClick={() => setBatchOpen(true)}
            >
              <Zap size={15} className="mr-1.5" /> Batch-generera
            </Button>
            <Button
              size="sm"
              data-testid={formOpen ? 'close-form-btn' : 'create-invoice-btn'}
              onClick={formOpen ? closeForm : openCreate}
              className="rounded-full"
            >
              {formOpen ? <><X size={15} className="mr-1.5" /> Avbryt</> : <><Plus size={15} className="mr-1.5" /> Ny faktura</>}
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={<Clock size={18} className="text-blue-600" />}
            label="Utestående"
            value={kpis.outstanding}
            color="bg-blue-50"
          />
          <KpiCard
            icon={<AlertCircle size={18} className="text-red-600" />}
            label="Förfallna"
            value={kpis.overdue}
            color="bg-red-50"
          />
          <KpiCard
            icon={<CheckCircle size={18} className="text-green-600" />}
            label="Betalda denna mån"
            value={kpis.paidThisMonth}
            color="bg-green-50"
          />
          <KpiCard
            icon={<FileText size={18} className="text-gray-600" />}
            label="Utkast"
            value={kpis.drafts}
            color="bg-gray-50"
          />
        </div>

        {/* Inline form */}
        {formOpen && (
          <InvoiceForm
            invoice={editInvoice}
            orgs={orgs}
            onSaved={handleSaved}
            onCancel={closeForm}
          />
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Kund</Label>
            <select
              className="h-9 px-3 border border-input rounded-md bg-background text-sm focus:outline-none"
              value={filterOrg}
              onChange={(e) => setFilterOrg(e.target.value)}
              data-testid="filter-org"
            >
              <option value="">Alla kunder</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <select
              className="h-9 px-3 border border-input rounded-md bg-background text-sm focus:outline-none"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="filter-status"
            >
              <option value="">Alla statusar</option>
              {Object.entries(STATUS_META).map(([v, m]) => (
                <option key={v} value={v}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <Card className="shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center text-muted-foreground text-sm" data-testid="loading-state">
                Laddar fakturor…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center space-y-2" data-testid="empty-state">
                <FileCheck size={40} className="mx-auto text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Inga fakturor hittades</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="invoices-table">
                  <thead>
                    <tr className="border-b" style={{ backgroundColor: '#f8fafc' }}>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Fakturanr</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Kund</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Period</th>
                      <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Totalt</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Förfaller</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((inv) => (
                      <React.Fragment key={inv.id}>
                        <tr
                          className="border-b hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => setExpandedId(expandedId === inv.id ? null : inv.id)}
                        >
                          <td className="px-4 py-3 font-mono text-xs font-semibold" style={{ color: '#1e293b' }}>
                            {inv.invoice_number}
                          </td>
                          <td className="px-4 py-3">{inv.org_name || '–'}</td>
                          <td className="px-4 py-3 text-muted-foreground">{BILLING_PERIOD_LABELS[inv.billing_period] || inv.billing_period}</td>
                          <td className="px-4 py-3 text-right font-semibold">{fmt(inv.total_amount)}</td>
                          <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                          <td className="px-4 py-3 text-muted-foreground">{fmtDate(inv.due_date)}</td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-1 justify-end">
                              {inv.status === 'utkast' && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Redigera"
                                    data-testid={`edit-${inv.id}`}
                                    onClick={() => openEdit(inv)}
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Skicka"
                                    data-testid={`send-${inv.id}`}
                                    onClick={() => setConfirm({ type: 'send', invoice: inv })}
                                  >
                                    <Send size={14} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Ta bort"
                                    data-testid={`delete-${inv.id}`}
                                    className="text-red-500 hover:text-red-600"
                                    onClick={() => setConfirm({ type: 'delete', invoice: inv })}
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </>
                              )}
                              {['skickad', 'förfallen'].includes(inv.status) && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Markera betald"
                                    data-testid={`markpaid-${inv.id}`}
                                    onClick={() => setMarkPaid({ invoice: inv })}
                                  >
                                    <DollarSign size={14} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Kreditera"
                                    data-testid={`credit-${inv.id}`}
                                    onClick={() => setConfirm({ type: 'credit', invoice: inv })}
                                  >
                                    <RefreshCcw size={14} />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedId === inv.id && (
                          <tr className="bg-slate-50/70">
                            <td colSpan={7} className="px-6 py-3">
                              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Rader</p>
                              {inv.lines?.length ? (
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-muted-foreground">
                                      <th className="text-left py-1 font-medium">Beskrivning</th>
                                      <th className="text-center py-1 font-medium">Antal</th>
                                      <th className="text-right py-1 font-medium">À-pris</th>
                                      <th className="text-right py-1 font-medium">Belopp</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {inv.lines.map((l) => (
                                      <tr key={l.id}>
                                        <td className="py-0.5">{l.description}</td>
                                        <td className="py-0.5 text-center">{l.quantity}</td>
                                        <td className="py-0.5 text-right">{fmt(l.unit_price)}</td>
                                        <td className="py-0.5 text-right">{fmt(l.amount)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="text-xs text-muted-foreground">Inga rader</p>
                              )}
                              {inv.notes && (
                                <p className="text-xs text-muted-foreground mt-2 italic">Not: {inv.notes}</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Batch dialog */}
      <BatchDialog
        open={batchOpen}
        onClose={() => setBatchOpen(false)}
        onGenerated={fetchAll}
      />

      {/* Confirm dialogs */}
      {confirm && (
        <ConfirmDialog
          open
          title={CONFIRM_META[confirm.type]?.title}
          message={CONFIRM_META[confirm.type]?.message}
          confirmLabel={CONFIRM_META[confirm.type]?.label}
          danger={CONFIRM_META[confirm.type]?.danger}
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Mark-paid dialog */}
      {markPaid && (
        <MarkPaidDialog
          invoice={markPaid.invoice}
          onDone={() => { setMarkPaid(null); fetchAll(); }}
          onCancel={() => setMarkPaid(null)}
        />
      )}
    </DashboardLayout>
  );
}
